import { WEBSITE_URL } from 'app/utils/constants/env.constants'
import { MAKE_UNIVERSAL_OPTION } from 'app/utils/constants/listing.constants'
import { USER_ROLE } from 'app/utils/constants/role.constants'
import { ACTION_STATUS } from 'app/utils/constants/status.constants'
import {
  isBothShippingType,
  isCollectShippingType,
  isShipOnlyShippingType
} from 'app/utils/helpers/shipping.helpers'

export const getAdditionalProperties = ({
  makes,
  models,
  generations,
  series,
  trims,
  part_number,
  shipping: { length }
}: ListingModel) => {
  return [
    {
      '@type': 'PropertyValue',
      name: 'makes',
      value: makes.map((make) => make.name).join(', ')
    },
    ...(!!models?.length
      ? [
          {
            '@type': 'PropertyValue',
            name: 'models',
            value: models.map((model) => model.name).join(', ')
          }
        ]
      : []),
    ...(!!generations?.length
      ? [
          {
            '@type': 'PropertyValue',
            name: 'generations',
            value: generations
              .map((generation) =>
                generation.year_begin
                  ? `${generation.name} [${generation.year_begin} - ${generation.year_end}]`
                  : generation.name
              )
              .join(', ')
          }
        ]
      : []),
    ...(!!series?.length
      ? [
          {
            '@type': 'PropertyValue',
            name: 'series',
            value: series.map((serie) => serie.name).join(', ')
          }
        ]
      : []),
    ...(!!trims?.length
      ? [
          {
            '@type': 'PropertyValue',
            name: 'trims',
            value: trims.map((trim) => trim.name).join(', ')
          }
        ]
      : []),
    ...(!!part_number
      ? [{ '@type': 'PropertyValue', name: 'part number', value: part_number }]
      : []),
    ...(!!length
      ? [{ '@type': 'PropertyValue', name: 'length', value: length }]
      : [])
  ].filter(Boolean)
}

export const getShippingDetails = (shipping: ListingModel['shipping']) => {
  const isShipOnly = isShipOnlyShippingType(shipping.shipping_types)
  const isCollect = isCollectShippingType(shipping.shipping_types)
  const isBoth = isBothShippingType(shipping.shipping_types)

  const isFreeShipping = shipping.make_shipping_free ?? false

  const shipOnlyDetails = {
    '@type': 'OfferShippingDetails',
    ...(isFreeShipping && {
      shippingRate: {
        '@type': 'MonetaryAmount',
        value: 0,
        currency: 'GBP'
      }
    }),
    shippingDestination: {
      '@type': 'DefinedRegion',
      addressCountry: 'GB'
    },
    deliveryTime: {
      '@type': 'ShippingDeliveryTime',
      transitTime: {
        '@type': 'QuantitativeValue',
        minValue: 2,
        maxValue: 5,
        unitCode: 'DAY'
      }
    },
    doesNotShip: false
  }

  const clickCollectDetails = {
    '@type': 'OfferShippingDetails',
    shippingDestination: {
      '@type': 'DefinedRegion',
      addressCountry: 'GB'
    },
    doesNotShip: true
  }

  return [
    ...(isBoth ? [shipOnlyDetails, clickCollectDetails] : []),
    ...(isShipOnly ? [shipOnlyDetails] : []),
    ...(isCollect ? [clickCollectDetails] : [])
  ]
}

export const getMerchantReturnPolicy = (shipping: ListingModel['shipping']) => {
  if (!shipping.accept_return)
    return {
      '@type': 'MerchantReturnPolicy',
      applicableCountry: 'GB',
      returnPolicyCategory: 'https://schema.org/MerchantReturnNotPermitted'
    }

  const isSellerReturn = shipping?.return_paid_by === ACTION_STATUS.seller
  const isShipOnly = isShipOnlyShippingType(shipping.shipping_types)
  const isBoth = isBothShippingType(shipping.shipping_types)
  const returnFees = isSellerReturn
    ? 'https://schema.org/FreeReturn'
    : 'https://schema.org/ReturnShippingFees'

  const oneShippingTypeReturn = isShipOnly
    ? 'https://schema.org/ReturnByMail'
    : 'https://schema.org/ReturnInStore'

  const returnMethod = isBoth
    ? 'https://schema.org/ReturnByMail'
    : oneShippingTypeReturn

  return {
    '@type': 'MerchantReturnPolicy',
    applicableCountry: 'GB',
    returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
    merchantReturnDays: shipping?.return_days,
    returnMethod: returnMethod,
    returnFees: returnFees
  }
}

export const getBrand = ({
  makes,
  categoryOptions
}: {
  makes: ListingModel['makes']
  categoryOptions: ListingModel['category_options']
}) => {
  const isUniversal =
    makes.length === 1 && makes[0].name === MAKE_UNIVERSAL_OPTION
  const fitmentBrand =
    categoryOptions?.find((option) => option.attribution.name.includes('Brand'))
      ?.name ?? null

  if (makes.length > 1 || isUniversal) return null

  return {
    '@type': 'Brand',
    name: fitmentBrand ?? makes[0].name
  }
}

export const getSellerDetails = (seller: ListingModel['user']) => {
  const isBusiness = seller.role === USER_ROLE.BUSINESS

  if (isBusiness) {
    return {
      seller: {
        '@type': 'Organization',
        name: seller?.store_name || seller?.contact_name,
        url: `${WEBSITE_URL}/store/${seller?.slug}/products`
      },
      offeredBy: {
        '@type': 'Organization',
        name: 'ifndautoparts',
        url: WEBSITE_URL
      }
    }
  }

  return {
    seller: {
      '@type': 'Organization',
      name: `${seller?.contact_name || seller?.full_name} on ifndautoparts`,
      url: WEBSITE_URL
    }
  }
}
